import React, { useContext } from "react";

import { commitLocalUpdate } from "relay-runtime";

import { ConnectionLabel } from "~/account/Integrations/Card";
import { AppContext } from "~/common";
import type { ActionItemIntegration, IntegrationProvider } from "~/integrations/actionItemIntegration/index";
import { environment } from "~/models";
import { Button } from "~/ui";
import { Modal } from "~/ui/legacyFellowIndex";
import { __globalModalContext } from "~/ui/modals";
import toasty from "~/ui/toast";

import styles from "../jira/css/IntegrationLink.module.css";

interface PromptInstallingJiraProps {
	integration: ActionItemIntegration;
	onConnect: () => void;
	onCancel: () => void;
	onError: (error: string) => void;
}

export function activateIntegrationInRelay(integrationKey: IntegrationProvider) {
	commitLocalUpdate(environment, store => {
		const viewer = store.get("viewer");
		const user = viewer?.getLinkedRecord("user");
		let connectedIntegrations = (user?.getValue("connectedActionItemIntegrations") as string[])?.slice() ?? [];

		connectedIntegrations = Array.from(new Set(connectedIntegrations).add(integrationKey));

		user?.setValue(connectedIntegrations, "connectedActionItemIntegrations");
	});
}

export function deactivateIntegrationInRelay(integrationKey: IntegrationProvider) {
	commitLocalUpdate(environment, store => {
		const viewer = store.get("viewer");
		const user = viewer?.getLinkedRecord("user");
		const connectedIntegrations = (user?.getValue("connectedActionItemIntegrations") as string[])?.slice() ?? [];

		const connectedIntegrationsSet = new Set(connectedIntegrations);

		connectedIntegrationsSet.delete(integrationKey);

		user?.setValue(Array.from(connectedIntegrationsSet), "connectedActionItemIntegrations");
	});
}

function PromptConnectingIntegration({ onConnect, onCancel, onError, integration }: PromptInstallingJiraProps) {
	const appContext = useContext(AppContext);

	const promptAuth = integration.promptAuth;

	const install = React.useCallback(() => {
		if (promptAuth) {
			promptAuth({ refresh: true })
				.then(() => {
					activateIntegrationInRelay(integration.key);
					toasty.success(`Connected ${integration.displayName}`);
					return onConnect();
				})
				.catch(function ({ message }) {
					toasty.error(message);
					return onError(message);
				});
		} else {
			onCancel();
			appContext.openSettingsModal("apps-and-integrations");
		}
	}, [promptAuth, integration.key, integration.displayName, onConnect, onError, onCancel, appContext]);

	const integrationPlusExternalActionItemName = `${integration.displayName} ${integration.externalActionItemName}s`;

	return (
		<Modal onClose={onCancel}>
			<Modal.Header>
				<Modal.Title className={styles.center}>
					{integration.logo} {integration.displayName}
				</Modal.Title>
				<Modal.HeaderActions onClose={onCancel} className={styles.center}>
					<ConnectionLabel connected={false} />
				</Modal.HeaderActions>
			</Modal.Header>
			<Modal.Body>
				<div>
					<p>
						Create, import, and manage {integrationPlusExternalActionItemName} from Fellow. With this integration, you
						can use Fellow for planning high-level action items from within your 1-on-1s and team meetings, while doing
						more granular project management using your existing workflows in {integration.displayName}.
					</p>
					<ul>
						<li>Create {integrationPlusExternalActionItemName} directly from action items in Fellow.</li>
						<li>Pull in existing {integrationPlusExternalActionItemName} as linked action items from any series.</li>
						<li>
							{/* eslint-disable-next-line max-len */}
							Use 2-way sync to keep issue titles, assignees, and due dates automatically in sync between Fellow and{" "}
							{integration.displayName}.
						</li>
						<li>
							Automatically mark {integrationPlusExternalActionItemName} as completed when a linked action item in
							Fellow is completed.
						</li>
					</ul>
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button size="normal" variant="primary" onClick={install}>
					Install
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

export function promptConnectingIntegrationModal(integration: ActionItemIntegration) {
	return new Promise<boolean>((resolve, reject) => {
		__globalModalContext?.open(({ close }) => {
			const onConnect = () => {
				resolve(true);
				close();
			};
			const onCancel = () => {
				resolve(false);
				close();
			};
			const onError = (message: string) => {
				reject(message);
				close();
			};
			return (
				<PromptConnectingIntegration
					// eslint-disable-next-line react/jsx-no-bind
					onConnect={onConnect}
					// eslint-disable-next-line react/jsx-no-bind
					onCancel={onCancel}
					// eslint-disable-next-line react/jsx-no-bind
					onError={onError}
					integration={integration}
				/>
			);
		});
	});
}
